import type { IInterceptor, ExecutionContext, CallHandler } from 'honorestjs'
import { Service, Reflector } from 'honorestjs'
import { validateEndpointInput, validateEndpointOutput, formatValidationError } from '@honorest/contract'
import { HTTPException } from 'hono/http-exception'
import { ContractMetadata } from '../decorators/contract.decorator'

/**
 * Interceptor that validates requests and responses against contract schemas
 *
 * This interceptor:
 * 1. BEFORE handler execution: Validates request input (params, query, body, headers)
 * 2. AFTER handler execution: Validates response output
 *
 * If validation fails:
 * - Input validation failure → throws HTTPException with 400 status
 * - Output validation failure → throws HTTPException with 500 status (prevents sending invalid data to client)
 *
 * The contract metadata is retrieved using the Reflector service for type-safe metadata access.
 *
 * @example
 * ```typescript
 * // Applied automatically by @Contract() decorator
 * @Contract(UsersContract.endpoints.getUser)
 * async getUser(@Param('id') id: string) {
 *   return this.userService.findById(id)
 * }
 * ```
 */
@Service()
export class ContractInterceptor implements IInterceptor {
	constructor(private readonly reflector: Reflector) {}

	async intercept(context: ExecutionContext, next: CallHandler): Promise<unknown> {
		// Get the handler name from context
		const handlerName = context.handler

		// Retrieve the contract for this handler using Reflector
		// The metadata is stored on the controller's prototype with the handler name as key
		const contract = this.reflector.get(ContractMetadata, context.controller.prototype[handlerName])

		// If no contract is registered, skip validation and proceed
		if (!contract) {
			return next.handle()
		}

		// Extract the Hono context
		const honoContext = context.context

		// === BEFORE HANDLER: Validate Input ===
		try {
			// Gather input data from the request
			const input = {
				params: honoContext.req.param(),
				query: honoContext.req.query(),
				// Only parse body for methods that typically have a body
				body: ['POST', 'PUT', 'PATCH'].includes(contract.method)
					? await this.parseBody(honoContext)
					: undefined,
				headers: this.extractHeaders(honoContext)
			}

			// Validate input against contract schemas
			const inputValidation = await validateEndpointInput(contract, input)

			if (!inputValidation.isValid) {
				// Collect detailed error information
				const errorDetails = inputValidation.errors.map((error) => ({
					field: error.field,
					message: error.message,
					code: error.code
				}))

				throw new HTTPException(400, {
					message: 'Request validation failed',
					cause: errorDetails
				})
			}
		} catch (error) {
			// Re-throw HTTPException as-is
			if (error instanceof HTTPException) {
				throw error
			}

			// Wrap other errors
			throw new HTTPException(400, {
				message: 'Invalid request format',
				cause: error
			})
		}

		// Execute the handler
		const result = await next.handle()

		// === AFTER HANDLER: Validate Output ===
		try {
			const outputValidation = await validateEndpointOutput(contract, result)

			if (!outputValidation.success) {
				// Log the error for debugging (output validation failures indicate server bugs)
				console.error('[ContractInterceptor] Output validation failed:', {
					handler: String(handlerName),
					endpoint: `${contract.method} ${contract.path}`,
					error: formatValidationError(outputValidation.error)
				})

				throw new HTTPException(500, {
					message: 'Internal server error: Invalid response format'
				})
			}
		} catch (error) {
			// Re-throw HTTPException as-is
			if (error instanceof HTTPException) {
				throw error
			}

			// Wrap other errors
			console.error('[ContractInterceptor] Unexpected error during output validation:', error)
			throw new HTTPException(500, {
				message: 'Internal server error',
				cause: error
			})
		}

		return result
	}

	/**
	 * Safely parses request body
	 * Returns undefined if parsing fails or body is empty
	 */
	private async parseBody(context: ExecutionContext['context']): Promise<unknown> {
		try {
			const contentType = context.req.header('content-type') || ''

			if (contentType.includes('application/json')) {
				return await context.req.json()
			}

			if (contentType.includes('application/x-www-form-urlencoded')) {
				return await context.req.parseBody()
			}

			if (contentType.includes('multipart/form-data')) {
				return await context.req.parseBody()
			}

			// Try JSON as default
			return await context.req.json().catch(() => undefined)
		} catch {
			return undefined
		}
	}

	/**
	 * Extracts headers from the request
	 * Converts Headers object to plain object
	 */
	private extractHeaders(context: ExecutionContext['context']): Record<string, string> {
		const headers: Record<string, string> = {}
		const rawHeaders = context.req.raw.headers

		if (rawHeaders && typeof rawHeaders.entries === 'function') {
			for (const [key, value] of rawHeaders.entries()) {
				headers[key] = value
			}
		}

		return headers
	}
}
